// Component: TradeLevelOverlay.jsx
// Purpose: Renders entry/SL/TP price lines, bands, or scoped lines on the chart
//          based on the active trade context and per-node visualization config.
//          Uses primitives (ISeriesPrimitive) for always-visible modes to avoid
//          chart.addSeries() which causes time-scale jumps. Price lines and hover
//          scoped lines use lightweight-charts native APIs.

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { LineStyle, LineSeries } from 'lightweight-charts';
import { TradeBandIndicator } from './plugins/TradeBandSeries';
import { toUnixSec, snapToBar } from '../../utils/tradeMarkerBuilder';
import { rejectReasonLabel } from '../../utils/rejectReason';
import { isFailedProtectionStatus } from '../../utils/tradeLevelUtils';

/*>>> LINE STYLE MAP <<<*/
const LINE_STYLE_MAP = {
    solid: LineStyle.Solid,
    dashed: LineStyle.Dashed,
    dotted: LineStyle.Dotted,
};

//--> Timestamp → Unix seconds via shared canonical helper (tradeMarkerBuilder.toUnixSec)

/**
 * TradeLevelOverlay — renders trade level visualization on the chart.
 *
 * Architecture:
 *   - Price lines: native series.createPriceLine() / applyOptions() (hover only)
 *   - Hover scoped lines: chart.addSeries(LineSeries) — created once, reused
 *   - Always-visible (both scoped_lines & band): single TradeBandIndicator primitive
 *     with transparent fills for scoped_lines, colored fills for band.
 *     Switching modes = applyOptions() on the primitive. Zero addSeries = zero time-scale jump.
 */
//--> Parse a chart timeframe string ('1m', '5m', '1h', '1d') to seconds. Used to
//*  pass the chart's bar size to the band primitive's forward-projection step.
const tfToSeconds = (tf) => {
    if (!tf || typeof tf !== 'string') return 60;
    const m = tf.match(/^(\d+)\s*([smhd])$/i);
    if (!m) return 60;
    const v = parseInt(m[1], 10);
    const u = m[2].toLowerCase();
    return u === 's' ? v : u === 'm' ? v * 60 : u === 'h' ? v * 3600 : v * 86400;
};

const REJECTED_LINE_COLOR = '#9CA3AF';   // 50-5: muted grey for an OMS-REJECTED SL/TP line

const TradeLevelOverlay = ({ series, chart, chartRef, activeTradeContext, allTrades, levelDisplayConfigs, mode = 'price_lines', hoverOnly = true, timeframe = '1m' }) => {
    //--> Refs for persistent chart objects (survive re-renders)
    const priceLinesRef = useRef({ entry: null, sl: null, tp: null });
    const scopedSeriesRef = useRef({ entry: null, sl: null, tp: null }); // hover scoped lines
    const scopedOptsRef = useRef({}); // last options applied per hover line series (skip no-op applyOptions)
    const bandRef = useRef(null); // shared primitive for always-visible scoped_lines AND band

    //--> Auto-switch: price_lines can't show multiple trades simultaneously
    const effectiveMode = useMemo(() => {
        if (!hoverOnly && mode === 'price_lines') return 'scoped_lines';
        return mode;
    }, [mode, hoverOnly]);

    //--> Resolve the first enabled config (with per-line style accessors)
    const resolveConfig = useCallback(() => {
        if (!levelDisplayConfigs) return null;
        for (const config of Object.values(levelDisplayConfigs)) {
            if (config.enabled) return config;
        }
        return null;
    }, [levelDisplayConfigs]);

    //--> Per-line style helpers: read from config.lines.{entry,sl,tp}
    const LINE_DEFAULTS = { entry: { color: '#FFFFFF', line_width: 2, line_style: 'solid' },
                            sl:    { color: '#EF4444', line_width: 2, line_style: 'dashed' },
                            tp:    { color: '#10B981', line_width: 2, line_style: 'dashed' } };
    const getLineStyle = useCallback((config, key) => {
        const s = config?.lines?.[key] || LINE_DEFAULTS[key];
        return {
            color: s.color,
            lineWidth: s.line_width || 2,
            lineStyle: LINE_STYLE_MAP[s.line_style] ?? LineStyle.Dashed,
        };
    }, []);

    // =====================================================================
    //  CLEANUP HELPERS
    // =====================================================================

    const clearPriceLines = useCallback(() => {
        if (!series) return;
        const refs = priceLinesRef.current;
        for (const key of ['entry', 'sl', 'tp']) {
            if (refs[key]) {
                try { series.removePriceLine(refs[key]); } catch { /* destroyed */ }
                refs[key] = null;
            }
        }
    }, [series]);

    const clearScopedSeries = useCallback(() => {
        if (!chart) return;
        const refs = scopedSeriesRef.current;
        for (const key of ['entry', 'sl', 'tp']) {
            if (refs[key]) {
                try { chart.removeSeries(refs[key]); } catch { /* destroyed */ }
                refs[key] = null;
                delete scopedOptsRef.current[key];
            }
        }
    }, [chart]);

    //--> Hide hover scoped series without removing (avoids addSeries on re-show)
    const hideScopedSeries = useCallback(() => {
        const refs = scopedSeriesRef.current;
        for (const key of ['entry', 'sl', 'tp']) {
            if (refs[key]) {
                try { refs[key].applyOptions({ visible: false }); } catch { /* destroyed */ }
                if (scopedOptsRef.current[key]) scopedOptsRef.current[key] = { ...scopedOptsRef.current[key], visible: false };
            }
        }
    }, []);

    //--> Clear band/primitive data (keeps primitive attached — no time-scale jump)
    const clearBand = useCallback(() => {
        if (bandRef.current) {
            bandRef.current.setData([]);
        }
    }, []);

    // =====================================================================
    //  MODE SWITCH — hide previous mode's visuals (no remove/add = no jump)
    // =====================================================================
    const prevModeRef = useRef(effectiveMode);
    const prevHoverOnlyRef = useRef(hoverOnly);
    useEffect(() => {
        if (prevModeRef.current !== effectiveMode) {
            if (prevModeRef.current === 'price_lines') clearPriceLines();
            if (prevModeRef.current === 'scoped_lines') { hideScopedSeries(); clearBand(); }
            if (prevModeRef.current === 'band') clearBand();
            prevModeRef.current = effectiveMode;
        }
        //--> Hover-Only flip with the SAME effective mode (scoped_lines ↔ scoped_lines, band ↔ band):
        //*   the hover path and the always-visible path draw through different objects
        //*   (LineSeries / price lines vs the shared primitive), so the one being left must be
        //*   cleared here — nothing else runs for it once its own effect early-returns.
        //*   Board #16: Scoped Lines stayed on screen after Hover Only was switched back ON.
        if (prevHoverOnlyRef.current !== hoverOnly) {
            if (hoverOnly) clearBand();                       // leaving always-visible
            else { hideScopedSeries(); clearPriceLines(); }   // leaving hover
            prevHoverOnlyRef.current = hoverOnly;
        }
    }, [effectiveMode, hoverOnly, clearPriceLines, hideScopedSeries, clearBand]);

    // =====================================================================
    //  PRICE LINES MODE — uses applyOptions() for seamless updates
    // =====================================================================
    useEffect(() => {
        if (effectiveMode !== 'price_lines' || !series) return;

        const config = resolveConfig();
        const ctx = activeTradeContext;
        const refs = priceLinesRef.current;

        //--> No trade hovered — remove existing price lines
        if (!ctx || !config?.enabled) {
            for (const key of ['entry', 'sl', 'tp']) {
                if (refs[key]) {
                    try { series.removePriceLine(refs[key]); } catch { /* ok */ }
                    refs[key] = null;
                }
            }
            return;
        }

        //--> Entry line (per-line style)
        const entryStyle = getLineStyle(config, 'entry');
        const entryOpts = {
            price: ctx.position.entry_price,
            color: entryStyle.color,
            lineWidth: entryStyle.lineWidth,
            lineStyle: entryStyle.lineStyle,
            axisLabelVisible: true,
            title: 'Entry',
            axisLabelColor: entryStyle.color,
            axisLabelTextColor: '#000000',
        };
        if (refs.entry) {
            refs.entry.applyOptions(entryOpts);
        } else {
            refs.entry = series.createPriceLine(entryOpts);
        }

        //--> Back-fill SL/TP for the hovered bar: on the ENTRY candle the
        //*   backend seeds level_history[0] with null SL/TP (exit nodes haven't
        //*   evaluated yet — they can't run until the position exists, which
        //*   is after the entry-bar evaluate() returns). Without a fallback,
        //*   the Entry line appears alone on the entry candle and SL/TP only
        //*   show from bar N+1 onward. The scoped_lines and band modes already
        //*   back-fill the same way; price_lines just needs to match.
        const firstSlPrice = ctx._levelHistory?.find(
            (e) => e?.levels?.stop_loss?.price != null,
        )?.levels?.stop_loss?.price ?? null;
        const firstTpPrice = ctx._levelHistory?.find(
            (e) => e?.levels?.take_profit?.price != null,
        )?.levels?.take_profit?.price ?? null;

        //--> SL line (per-line style). Falls back to the trade's first known
        //*   SL level so the line is present on the entry candle.
        const slPrice = ctx.levels?.stop_loss?.price ?? firstSlPrice;
        if (slPrice != null) {
            const slStyle = getLineStyle(config, 'sl');
            //! 10509-6/E13/D31 — display-only "BE"/"Locked" suffix on the SL line label when the
            //  backend has derived the standing SL's current trigger is at/beyond entry in the
            //  profit direction. Read straight off ctx.levels.stop_loss.lock_state (no re-derivation).
            const slLockState = ctx.levels?.stop_loss?.lock_state;
            //! 50-5 R3 -- an OMS-REJECTED SL draws muted + dotted with its reject label. Read from the
            //  trade's LAST level entry so it holds on every hovered bar.
            const slRej = ctx._levelHistory?.at(-1)?.levels?.stop_loss;
            const slRejected = isFailedProtectionStatus(slRej?.leg_status);
            const slTitle = slRej?.leg_status === 'NOT_PLACED'
                ? 'SL not placed'
                : slRejected
                ? `SL rejected · ${rejectReasonLabel(slRej.reject_reason) ?? 'broker reject'}`
                : slLockState === 'BE' ? 'SL BE' : slLockState === 'LOCKED' ? 'SL Locked' : 'SL';
            const slColor = slRejected ? REJECTED_LINE_COLOR : slStyle.color;
            const slOpts = {
                price: slPrice,
                color: slColor,
                lineWidth: slStyle.lineWidth,
                lineStyle: slRejected ? LineStyle.Dotted : slStyle.lineStyle,
                axisLabelVisible: true,
                title: slTitle,
                axisLabelColor: slColor,
                axisLabelTextColor: '#FFFFFF',
            };
            if (refs.sl) {
                refs.sl.applyOptions(slOpts);
            } else {
                refs.sl = series.createPriceLine(slOpts);
            }
        } else if (refs.sl) {
            try { series.removePriceLine(refs.sl); } catch { /* ok */ }
            refs.sl = null;
        }

        //--> TP line (per-line style). Same back-fill rationale as SL.
        const tpPrice = ctx.levels?.take_profit?.price ?? firstTpPrice;
        if (tpPrice != null) {
            const tpStyle = getLineStyle(config, 'tp');
            //! 10509-6/E13/D31/Q3 — a TP held for the exchange price band (no standing order yet
            //  accepted) draws DASHED-DISTINCT (dotted, vs the normal dashed TP line) with a
            //  "waiting" label suffix, straight off ctx.levels.take_profit.leg_status. Never
            //  re-derived client-side — backend (`_resolve_current_edge`) is the sole authority.
            const tpHeld = ctx.levels?.take_profit?.leg_status === 'HELD_PRICE_BAND';
            //! 50-5 R3 -- same rejected branch as SL (read from the trade's LAST level entry).
            const tpRej = ctx._levelHistory?.at(-1)?.levels?.take_profit;
            const tpRejected = isFailedProtectionStatus(tpRej?.leg_status);
            const tpTitle = tpRej?.leg_status === 'NOT_PLACED'
                ? 'TP not placed'
                : tpRejected
                ? `TP rejected · ${rejectReasonLabel(tpRej.reject_reason) ?? 'broker reject'}`
                : tpHeld ? 'TP (waiting – band)' : 'TP';
            const tpColor = tpRejected ? REJECTED_LINE_COLOR : tpStyle.color;
            const tpOpts = {
                price: tpPrice,
                color: tpColor,
                lineWidth: tpStyle.lineWidth,
                lineStyle: (tpHeld || tpRejected) ? LineStyle.Dotted : tpStyle.lineStyle,
                axisLabelVisible: true,
                title: tpTitle,
                axisLabelColor: tpColor,
                axisLabelTextColor: '#FFFFFF',
            };
            if (refs.tp) {
                refs.tp.applyOptions(tpOpts);
            } else {
                refs.tp = series.createPriceLine(tpOpts);
            }
        } else if (refs.tp) {
            try { series.removePriceLine(refs.tp); } catch { /* ok */ }
            refs.tp = null;
        }
    }, [effectiveMode, series, activeTradeContext, levelDisplayConfigs, resolveConfig, getLineStyle]);

    // =====================================================================
    //  SCOPED LINES MODE (hover) — create once, update via applyOptions + setData
    // =====================================================================
    useEffect(() => {
        if (effectiveMode !== 'scoped_lines' || !hoverOnly || !chart || !series) return;

        const config = resolveConfig();
        const ctx = activeTradeContext;
        const refs = scopedSeriesRef.current;

        //--> No trade hovered — clear data from existing series (don't remove them)
        if (!ctx?._levelHistory || !config?.enabled) {
            for (const key of ['entry', 'sl', 'tp']) {
                if (refs[key]) {
                    try { refs[key].setData([]); } catch { /* ok */ }
                }
            }
            return;
        }

        const history = ctx._levelHistory;

        //--> applyOptions() makes lightweight-charts re-fire the crosshair event synchronously;
        //*   SmartChart answers with a NEW activeTradeContext, this effect re-runs, and React
        //*   hits "Maximum update depth exceeded" (board #16). Only touch options when the
        //*   style or visibility actually changed — data refresh alone is setData().
        const ensureSeries = (key) => {
            const ls = getLineStyle(config, key);
            const next = { color: ls.color, lineWidth: ls.lineWidth, lineStyle: ls.lineStyle, visible: true };
            const applied = scopedOptsRef.current[key];
            if (!refs[key]) {
                refs[key] = chart.addSeries(LineSeries, {
                    ...next,
                    lastValueVisible: false, priceLineVisible: false,
                    crosshairMarkerVisible: false,
                    autoscaleInfoProvider: () => null,
                });
                scopedOptsRef.current[key] = next;
            } else if (!applied || Object.keys(next).some(k => applied[k] !== next[k])) {
                refs[key].applyOptions(next);
                scopedOptsRef.current[key] = next;
            }
            return refs[key];
        };

        //--> Back-fill leading null SL/TP from first non-null values
        //    so lines start from the entry candle (matches primitive path behavior)
        const firstSl = history.find(e => e.levels?.stop_loss?.price != null)?.levels?.stop_loss?.price;
        const firstTp = history.find(e => e.levels?.take_profit?.price != null)?.levels?.take_profit?.price;

        //--> LineSeries.setData requires STRICTLY ascending times (lightweight-charts asserts,
        //*   and the assertion escapes the effect → ChartErrorBoundary). level_history can carry
        //*   two snapshots on one bar (entry-bar seed + the evaluator's same-bar snapshot on a
        //*   deferred next-open fill; every persisted backtest run before 2026-09-08 has it).
        //*   One point per timestamp, the LAST snapshot wins — it is the evaluator's, with real
        //*   levels. Board #16.
        const toSeriesData = (valueOf) => {
            const byTime = new Map();
            for (const e of history) {
                const time = toUnixSec(e.timestamp);
                const value = valueOf(e);
                if (time == null || value == null) continue;
                byTime.set(time, { time, value });
            }
            return [...byTime.values()].sort((a, b) => a.time - b.time);
        };

        //--> Entry
        ensureSeries('entry').setData(toSeriesData(e => e.position.entry_price));

        //--> SL (back-fill leading nulls with first known value)
        ensureSeries('sl').setData(toSeriesData(e => e.levels?.stop_loss?.price ?? firstSl));

        //--> TP (back-fill leading nulls with first known value)
        ensureSeries('tp').setData(toSeriesData(e => e.levels?.take_profit?.price ?? firstTp));
    }, [effectiveMode, hoverOnly, chart, series, activeTradeContext, levelDisplayConfigs, resolveConfig, getLineStyle]);

    // =====================================================================
    //  SHARED HELPERS for primitive-based modes (always-visible scoped_lines & band)
    // =====================================================================

    //--> Build band options — transparent fills for scoped_lines, colored for band
    const buildBandOptions = useCallback((config, forMode) => {
        const entryLS = getLineStyle(config, 'entry');
        const slLS = getLineStyle(config, 'sl');
        const tpLS = getLineStyle(config, 'tp');
        const isBand = forMode === 'band';
        return {
            slColor: slLS.color,
            tpColor: tpLS.color,
            slFillColor: isBand ? slLS.color + '26' : 'transparent',
            tpFillColor: isBand ? tpLS.color + '26' : 'transparent',
            entryColor: entryLS.color,
            entryLineWidth: entryLS.lineWidth,
            levelLineWidth: slLS.lineWidth,
        };
    }, [getLineStyle]);

    //--> Convert a single trade's level_history to band segment format.
    //*   Prepend a synthetic entry-bar point so single-snapshot trades (trades
    //*   that closed within one bar of entry) still render as a 2+ point segment
    //*   — TradeBandSeries drops segments with <2 points. The entry-bar point
    //*   uses the first known SL/TP from level_history (back-fill), which matches
    //*   the scoped_lines back-fill semantic and the graph_evaluator contract:
    //*   on the entry bar SL/TP nodes haven't evaluated yet, so the next-bar
    //*   snapshot is the earliest authoritative level reading.
    const toBandSegment = useCallback((trade) => {
        const levelHistory = trade?.level_history || [];

        //--> Pre-fill suppression: a live trade with no fill_bar_start yet
        //*   is still waiting for the tick trigger. Its only snapshot is the
        //*   preliminary (entry=bar.close, null SL/TP) which would otherwise
        //*   render as a flashing entry-line at the SIGNAL bar — user reported
        //*   it as "temporary priceline before the trade fills". Returning []
        //*   keeps the band silent until ORDER_FILLED stamps fill_bar_start.
        //*   Historic trades (order-levels API) have exit_time set, so this
        //*   guard only matches the live pre-fill window.
        const isLivePreFill = (
            trade?.fill_bar_start == null
            && (trade?.exit_time == null && trade?.exitTime == null)
        );
        if (isLivePreFill) return [];

        //--> 50-5 R3: rejected flags come from the trade's LAST level entry (current state), stamped on
        //*   every point so TradeBandSeries can skip the fill zone and stroke muted + dotted.
        const lastLevels = levelHistory.length ? levelHistory[levelHistory.length - 1]?.levels : null;
        const slRejected = isFailedProtectionStatus(lastLevels?.stop_loss?.leg_status);
        const tpRejected = isFailedProtectionStatus(lastLevels?.take_profit?.leg_status);
        let points = levelHistory.map(e => ({
            time: toUnixSec(e.timestamp),
            sl: e.levels?.stop_loss?.price ?? null,
            tp: e.levels?.take_profit?.price ?? null,
            entry: e.position.entry_price,
            slRejected,
            tpRejected,
        })).filter(p => p.time != null);

        //--> Anchor precedence:
        //*   1. fill_bar_start  — FILL bar (set by fill_event_consumer when
        //*      ORDER_FILLED arrives). Prefer this so the band starts on the
        //*      actual entry candle, not the signal-arrival candle.
        //*   2. entry_time (snake)  — legacy shape from level_history ctx /
        //*      trade_context. Points at SIGNAL bar (preserved deliberately
        //*      so the preliminary snapshot's republish dedups correctly).
        //*   3. entryTime (camel)   — tradingTransforms trade shape (historic).
        //--> 50-5 R2: with a fill_time, the band starts on the chart bar CONTAINING the fill instant
        //*   (snapped on the bar-of-record grid, never earlier than it); otherwise the old anchor.
        const entryTime = trade?.fill_time
            ? snapToBar(trade.fill_time, timeframe, trade.fill_bar_start ?? trade.entry_time)
            : toUnixSec(
                trade?.fill_bar_start
                ?? trade?.entry_time
                ?? trade?.entryTime
            );
        //--> Drop points strictly before the anchor, keeping the LATEST
        //*   pre-anchor snapshot as a "seed" so we can synthesize a starting
        //*   point at entryTime if the post-filter window has nothing there.
        //*   Background: the fill-republish backdates its snapshot to the
        //*   signal bar so it dedups with the preliminary. After filtering,
        //*   that authoritative snapshot (real SL/TP, fill_price) would be
        //*   dropped and — if the next bar hasn't closed yet — the segment
        //*   is empty, the band fails the ≥2-point gate and doesn't render.
        //*   Seeding the anchor with the dropped snapshot's data gives the
        //*   projection one raw point to extrapolate from, so the band shows
        //*   immediately at the fill candle.
        let preAnchorSeed = null;
        if (entryTime != null) {
            const filtered = [];
            for (const p of points) {
                if (p.time < entryTime) preAnchorSeed = p;
                else filtered.push(p);
            }
            points = filtered;
        }
        if (entryTime != null) {
            const firstPoint = points[0];
            const needAnchor = !firstPoint || firstPoint.time > entryTime;
            if (needAnchor) {
                const seed = preAnchorSeed || firstPoint;
                if (seed) {
                    const seededSl = seed.sl ?? points.find(p => p.sl != null)?.sl ?? null;
                    const seededTp = seed.tp ?? points.find(p => p.tp != null)?.tp ?? null;
                    points.unshift({
                        time: entryTime,
                        sl: seededSl,
                        tp: seededTp,
                        entry: seed.entry,
                        slRejected,
                        tpRejected,
                    });
                }
            }
        }

        const exitTime = toUnixSec(trade?.exit_time ?? trade?.exitTime);
        if (exitTime != null && points.length >= 1) {
            const lastPoint = points[points.length - 1];
            //--> Extend the band exactly to the exit candle if the last snapshot preceded it.
            //    This fixes cases where the trade closed before the next level_history snapshot
            //    was taken, causing the band to drop a candle early.
            if (lastPoint.time < exitTime) {
                points.push({
                    time: exitTime,
                    sl: lastPoint.sl,
                    tp: lastPoint.tp,
                    entry: lastPoint.entry,
                    slRejected,
                    tpRejected,
                });
            }
        }

        return points;
    }, [timeframe]);

    // =====================================================================
    //  BAND (hover) — show primitive for hovered trade only
    //  (hover scoped_lines uses LineSeries above — keep them separate to avoid duplicate rendering)
    // =====================================================================
    useEffect(() => {
        if (effectiveMode !== 'band' || !hoverOnly || !series || !chart) return;

        const ctx = activeTradeContext;
        if (!ctx?._levelHistory) {
            if (bandRef.current) bandRef.current.setData([]);
            return;
        }

        const config = resolveConfig();
        if (!config?.enabled) return;

        //--> Single trade segment wrapped in array (per-trade isolation).
        //*   toBandSegment consumes the full trade shape so it can prepend the
        //*   entry-bar point for single-snapshot trades.
        //--> ctx._trade is the hovered trade itself (SmartChart crosshair handler). It
        //*   carries exit_time, which toBandSegment's live pre-fill guard needs: backtest
        //*   snapshots never have fill_bar_start, so without exit_time every hovered
        //*   backtest trade looked "not filled yet" and the hover band drew nothing
        //*   (board #16, Deepak 2026-09-08).
        const segment = toBandSegment({
            ...(ctx._trade || {}),
            level_history: ctx._levelHistory,
            //--> fill_bar_start = FILL candle, set by fill_event_consumer.
            //*   entry_time = SIGNAL candle (kept for dedup). toBandSegment
            //*   prefers fill_bar_start so the band starts on the entry
            //*   candle, not one candle before it.
            fill_bar_start: ctx.position?.fill_bar_start ?? ctx._trade?.fill_bar_start,
            entry_time: ctx.position?.entry_time ?? ctx._trade?.entry_time,
        });
        const opts = buildBandOptions(config, effectiveMode);

        if (!bandRef.current) {
            bandRef.current = new TradeBandIndicator(opts);
            series.attachPrimitive(bandRef.current);
        } else {
            bandRef.current.applyOptions(opts);
        }

        bandRef.current.setData([segment]);
    }, [effectiveMode, hoverOnly, series, chart, activeTradeContext, resolveConfig, buildBandOptions, toBandSegment]);

    // =====================================================================
    //  BAND / SCOPED_LINES (always-visible) — single primitive, toggle fills via applyOptions
    //  Uses ISeriesPrimitive (canvas) instead of chart.addSeries() to avoid time-scale jumps.
    // =====================================================================
    useEffect(() => {
        if (hoverOnly || !series || !chart || !allTrades) return;
        if (effectiveMode !== 'scoped_lines' && effectiveMode !== 'band') return;

        const config = resolveConfig();
        if (!config?.enabled) {
            if (bandRef.current) bandRef.current.setData([]);
            return;
        }

        //--> Build array of segments — one per trade (NOT merged). Parallel openFlags
        //*   marks open trades (exit_time null/undefined) so TradeBandIndicator extends
        //*   the band N bars ahead of the live bar. Backtest trades always have exit_time
        //*   set → openFlags[i] = false → no projection. Live/paper open positions set
        //*   exit_time = null → openFlags[i] = true → projection.
        const segments = [];
        const openFlags = [];
        for (const trade of allTrades) {
            if (!trade.level_history || trade.level_history.length === 0) continue;
            segments.push(toBandSegment(trade));
            openFlags.push(trade.exit_time == null);
        }

        //--> Build options: transparent fills for scoped_lines, colored for band
        const opts = buildBandOptions(config, effectiveMode);

        if (!bandRef.current) {
            bandRef.current = new TradeBandIndicator(opts);
            series.attachPrimitive(bandRef.current);
        } else {
            //--> applyOptions toggles fill colors on mode switch — no addSeries, no jump
            bandRef.current.applyOptions(opts);
        }

        bandRef.current.setData(segments, openFlags);
        //--> Forward projection — only when at least one open trade exists.
        //*  Goal: 2 visible empty bars of buffer to the right of the live candle.
        //*  The band primitive queries the chart's right-edge bar time and
        //*  emits synthetic bars until reaching chart_last_bar + trailBars × chartBarSec.
        //*
        //*  trailBars=3 (not 2) because chart_last_bar.time is the LIVE candle's
        //*  START time:
        //*    +1 × barSec = flush with the live candle's right edge (0 buffer)
        //*    +2 × barSec = 1 visible empty bar of buffer
        //*    +3 × barSec = 2 visible empty bars of buffer ← desired
        const chartBarSec = tfToSeconds(timeframe);
        const trailBars = openFlags.some(Boolean) ? 3 : 0;
        bandRef.current.setProjection({ trailBars, barSizeSec: chartBarSec });
    }, [effectiveMode, hoverOnly, series, chart, allTrades, levelDisplayConfigs, resolveConfig, buildBandOptions, toBandSegment]);

    // =====================================================================
    //  UNMOUNT — clean up everything
    // =====================================================================
    useEffect(() => {
        return () => {
            //--> Guard: In a deleted subtree React runs parent cleanups FIRST
            //    (preorder traversal), so ChartPane's cleanup calls chart.remove()
            //    and sets chartInstanceRef.current = null BEFORE this runs.
            //    Calling detachPrimitive / removeSeries on a destroyed chart
            //    schedules an rAF that crashes with "Object is disposed".
            //    Only mutate the chart if it's still alive.
            if (chartRef?.current) {
                clearPriceLines();
                clearScopedSeries();
                if (bandRef.current && series) {
                    try { series.detachPrimitive(bandRef.current); } catch { /* destroyed */ }
                }
            }
            bandRef.current = null;
        };
    }, [clearPriceLines, clearScopedSeries, series, chartRef]);

    return null;
};

export default TradeLevelOverlay;
